import { Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAIExtraction } from '@/hooks/useAIExtraction';
import AIExtractionUpload from '@/components/AIExtraction/AIExtractionUpload';
import AIExtractionJobList from '@/components/AIExtraction/AIExtractionJobList';
import AIExtractionJobDetail from '@/components/AIExtraction/AIExtractionJobDetail';

import { useTranslation } from 'react-i18next';

const AIExtractionPage: React.FC = () => {
  const [showJobs, setShowJobs] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = location.state as
    | {
        initialText?: string;
        source?: Record<string, unknown>;
      }
    | null
    | undefined;
  const initialTextFromNavigation = navigationState?.initialText || '';
  const { t } = useTranslation();

  const {
    jobs,
    selectedJob,
    isLoading,
    isUploading,
    error,
    uploadDocument,
    fetchJobs,
    selectJob,
    approveJob,
    rejectJob,
    deleteAllJobs,
    clearError,
  } = useAIExtraction();

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleUpload = async (file: File | string, requestedAction: string, context?: Record<string, unknown>) => {
    await uploadDocument(file, requestedAction, context);
    setShowJobs(true); // Show jobs section after upload
    // Jobs will be refreshed automatically by the hook
  };

  const handleSelectJob = async (jobId: string) => {
    await selectJob(jobId);
  };

  const handleApprove = async () => {
    if (!selectedJob) return;
    
    try {
      // Get the updated job with extraction_result
      const updatedJob = await approveJob(selectedJob.id);
      
      // Navigate to the created/updated entity
      const action = updatedJob.requested_action;
      const extractionResult = updatedJob.extraction_result || {};
      const context = updatedJob.context || {};
      
      if (action === 'create_usecase' || action === 'update_usecase') {
        const usecaseId = extractionResult.usecase_id || context.usecase_id;
        if (usecaseId) {
          navigate(`/cas-usage/${usecaseId}`);
          return;
        }
      } else if (action === 'create_cadrage' || action === 'update_cadrage') {
        const cadrageId = extractionResult.cadrage_id || context.cadrage_id;
        if (cadrageId) {
          navigate(`/cadrages/${cadrageId}`);
          return;
        }
        // Fallback to use case if cadrage ID is not available
        const usecaseId = extractionResult.usecase_id || context.usecase_id;
        if (usecaseId) {
          navigate(`/cas-usage/${usecaseId}`);
          return;
        }
      }
    } catch (error) {
      // Error is already handled by the hook
      console.error('Approval failed:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedJob) return;
    await rejectJob(selectedJob.id);
  };

  const handleDeleteAll = async () => {
    if (jobs.length === 0) return;

    const confirmed = window.confirm(
      t('aiExtraction.deleteAllConfirmation', { count: jobs.length })
    );

    if (confirmed) {
      await deleteAllJobs();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t('aiExtraction.pageTitle')}</h1>
        </div>
        <p className="text-muted-foreground">
          {t('aiExtraction.pageDescription')}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={clearError}
              className="text-destructive hover:text-destructive/80 text-sm font-medium"
            >
              {t('global.close')}
            </button>
          </div>
        </div>
      )}

      {/* Upload Form - Full Width (hidden after first upload) */}
      {!showJobs && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <AIExtractionUpload
            onUpload={handleUpload}
            isUploading={isUploading}
            initialText={initialTextFromNavigation}
          />
        </div>
      )}

      {/* Jobs Section - Full Width at Bottom (shown after first upload) */}
      {showJobs && (
        <>
          {/* Restart Button */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => setShowJobs(false)}
              className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
            >
              ← Nouveau document
            </button>
          </div>

          {/* Job Detail Preview (before jobs list) */}
          {selectedJob && (
            <div className="mb-6">
              <AIExtractionJobDetail
                job={selectedJob}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={isLoading}
              />
            </div>
          )}

          {/* Job List */}
          <AIExtractionJobList
            jobs={jobs}
            selectedJobId={selectedJob?.id || null}
            onSelectJob={handleSelectJob}
            onDeleteAll={() => {
              handleDeleteAll();
              setShowJobs(false);
            }}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
};

export default AIExtractionPage;


